import { useRouter } from "@-/com.npmjs.expo-router";
import { Platform } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { persistentKeyboardDismiss } from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import type {
  LearnCardDestination,
  LearnCardDestinationResolution,
} from "@-/dev.improve.haniverse.lib.learn.destination";
import { useVoiceStudyController } from "@-/dev.improve.haniverse.lib.voice-study";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useDrillPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill.navigator";
import { useLearnDeckPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.navigator";
import { useNewMorphemeLessonPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme.navigator";
import { useSlideLessonPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index.navigator";
import { useLearnSummaryPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import { useCallback } from "react";

export type LearnCardNavigationMode = "push" | "replace";

/**
 * Turns a resolved destination into a route change — the ONLY place the Learn flow maps
 * destinations to screens, so the deck (which mounts the destination instead) and the router
 * can never disagree about what a destination means.
 *
 * `replace` is the review flow's mode: a review session is one logical screen advancing through
 * cards, and pushing stacked a spent screen per card (duplicate ratings from swipe-back, a refetch
 * storm per rating). The lesson flow pushes, so the lesson stack keeps its back history.
 *
 * Side effects that belong to ARRIVING somewhere (not to the action that got us here) live here:
 * dismissing the persistent keyboard before a lesson, slide, summary or summary can
 * be covered by it; recording that the due queue was exhausted; the voice-complete turn.
 */
export function useNavigateToLearnCardDestination(): (
  resolution: LearnCardDestinationResolution,
  options: { mode: LearnCardNavigationMode },
) => Promise<void> {
  const { arrive, navigateRoute } = useLearnRouteDestinationNavigation();
  const drillPageNavigator = useDrillPageNavigator();
  const lessonPageNavigator = useNewMorphemeLessonPageNavigator();
  const slidePageNavigator = useSlideLessonPageNavigator();
  const deckPageNavigator = useLearnDeckPageNavigator();
  const [isFastForwardEnabled] = useContextSelectors(NavContext, (c) => c.isFastForwardEnabled);

  const navigatorsRef = useLatest({
    deckPageNavigator,
    drillPageNavigator,
    lessonPageNavigator,
    slidePageNavigator,
  });
  const isFastForwardEnabledRef = useLatest(isFastForwardEnabled);

  return useCallback(
    async (
      resolution: LearnCardDestinationResolution,
      { mode }: { mode: LearnCardNavigationMode },
    ) => {
      const navigators = navigatorsRef.current;
      arrive(resolution);
      const { destination } = resolution;
      if (destination.kind === "summary" || destination.kind === "exit") {
        await navigateRoute(destination);
        return;
      }
      await navigateTo(destination, {
        isFastForwardEnabled: isFastForwardEnabledRef.current,
        mode,
        navigators,
      });
    },
    [arrive, isFastForwardEnabledRef, navigateRoute, navigatorsRef],
  );
}

type LearnRouteDestination = Extract<LearnCardDestination, { kind: "summary" | "exit" }>;

/** The deck mounts review, lesson and slide cards itself; only its terminal routes navigate. */
export function useNavigateToLearnRouteDestination(): (
  resolution: LearnCardDestinationResolution,
) => Promise<void> {
  const { arrive, navigateRoute } = useLearnRouteDestinationNavigation();
  return useCallback(
    async (resolution: LearnCardDestinationResolution) => {
      const { destination } = resolution;
      if (destination.kind !== "summary" && destination.kind !== "exit") {
        throw new Error("Learn deck card destinations must be mounted, not routed");
      }
      arrive(resolution);
      await navigateRoute(destination);
    },
    [arrive, navigateRoute],
  );
}

function useLearnRouteDestinationNavigation() {
  const router = useRouter();
  const voiceStudyController = useVoiceStudyController();
  const summaryNavigator = useLearnSummaryPageNavigator();
  const [setHasFinishedReviewsThisSession] = useContextSelectors(
    NavContext,
    (c) => c.setHasFinishedReviewsThisSession,
  );
  const navigatorsRef = useLatest({ router, summaryNavigator, voiceStudyController });
  const setHasFinishedReviewsThisSessionRef = useLatest(setHasFinishedReviewsThisSession);
  const arrive = useCallback(
    ({ dueQueueExhausted }: LearnCardDestinationResolution) => {
      if (dueQueueExhausted) {
        // End the keyboard session before preparing the destination that will replace it.
        persistentKeyboardDismiss();
        setHasFinishedReviewsThisSessionRef.current(true);
      }
    },
    [setHasFinishedReviewsThisSessionRef],
  );
  const navigateRoute = useCallback(
    async (destination: LearnRouteDestination) => {
      const navigators = navigatorsRef.current;
      captureAnalyticsEvent("learn_flow_ended", {
        reason: destination.kind === "summary" ? destination.reason : "exit",
      });
      // Leaving the lesson stack means returning to the summary. On native the deck keeps its
      // history in its own state, so the deck is ONE stack entry and `back()` pops it. On web every deck card is its own pushed browser-history entry, so
      // `back()` only stepped the deck back one card — undoing that card's action — and the learner
      // bounced between their last two cards instead of reaching the summary (e.g. a free learner
      // at the daily cap leaving Level 2's start slide). Web leaves the way a summary destination
      // does, which keeps the deck's history behind it for the browser's Back.
      if (destination.kind === "exit" && Platform.OS !== "web") {
        navigators.router.back();
        return;
      }
      if (destination.kind === "summary" && destination.reason === "voice-complete") {
        navigators.voiceStudyController.unlock();
        navigators.voiceStudyController.setTurn({
          id: "voice-complete",
          prompt: {
            text: "Voice review complete. There are no more due cards with spoken component clues.",
          },
          side: "front",
        });
      } else {
        persistentKeyboardDismiss();
      }
      await navigators.summaryNavigator.replace.mutateAsync();
    },
    [navigatorsRef],
  );
  return { arrive, navigateRoute };
}

type Navigators = {
  deckPageNavigator: ReturnType<typeof useLearnDeckPageNavigator>;
  drillPageNavigator: ReturnType<typeof useDrillPageNavigator>;
  lessonPageNavigator: ReturnType<typeof useNewMorphemeLessonPageNavigator>;
  slidePageNavigator: ReturnType<typeof useSlideLessonPageNavigator>;
};

async function navigateTo(
  destination: Exclude<LearnCardDestination, LearnRouteDestination>,
  {
    isFastForwardEnabled,
    mode,
    navigators,
  }: { isFastForwardEnabled: boolean; mode: LearnCardNavigationMode; navigators: Navigators },
): Promise<void> {
  switch (destination.kind) {
    case "review": {
      await navigators.drillPageNavigator[mode].mutateAsync({
        drillType: destination.card.drillType,
        grapheme: destination.card.grapheme,
      });
      return;
    }
    case "lesson": {
      persistentKeyboardDismiss();
      // Fast-forward skips the lesson FRONT and lands on the mnemonics. It does NOT enroll:
      // enrollment is the card's own "Next" (accept) action, so a character the learner then
      // skips until tomorrow was never silently added to study.
      await navigators.lessonPageNavigator[mode].mutateAsync({
        face: isFastForwardEnabled ? "back" : "front",
        morpheme: destination.lesson.graphemeCard.front,
      });
      return;
    }
    case "slide": {
      persistentKeyboardDismiss();
      await navigators.slidePageNavigator[mode].mutateAsync({ slideSlug: destination.lesson.slug });
      return;
    }
    case "quiz": {
      persistentKeyboardDismiss();
      // A quiz has no route of its own: it opens on the deck, like every card there.
      await navigators.deckPageNavigator[mode].mutateAsync({
        kind: "quiz",
        quizSlug: destination.quizSlug,
        source: destination.source,
      });
      return;
    }
  }
}
